// Sidebar navigation configuration — extracted from components/Layout.jsx to
// keep the layout file focused on rendering. Import back into Layout.jsx.
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Building2,
  Map,
  CalendarClock,
  ClipboardCheck,
  AlertOctagon,
  FileSignature,
  UserCheck,
  Wallet,
  UserCog,
  Clock,
  MapPin,
  Send,
  FileBarChart2,
  History,
  Globe,
  Radio,
  CalendarDays,
  Palmtree,
  AlertTriangle,
  Mail,
  ScrollText,
  ShieldAlert,
  Wrench,
  Route,
  Database,
  Sliders,
  Sparkles,
  Inbox,
  MessageSquareWarning,
  ShieldCheck,
  Coffee,
  ClipboardList,
  Settings2,
  BellRing,
  UserPlus,
  Tag,
  Gauge,
  HelpCircle,
} from "lucide-react";
import { readGeometry } from "./popoutGeometry";

// iter277 — Every leaf entry declares a `permission` slug so custom
// roles automatically get a role-appropriate sidebar. The permission
// vocabulary lives in `backend/permissions.py` (`RESOURCES × ACTIONS`)
// and every slug used here is already valid there. Built-in roles won't
// change behaviour because their permission templates already grant the
// slugs this file asks for.
//
// Rules that STILL matter alongside the new gate:
//   • `hideForClient` — extra safety for portal-tier users whose roles
//     do inherit read perms but shouldn't see internal ops screens.
//   • `guardOK: true` — opts a leaf in for the guard role (their built-in
//     template grants only a narrow slice of perms, and this flag lets
//     specific leaves like /guard-portal render even when the perm
//     filter alone wouldn't say yes).
//   • `adminOnly` / `adminScope` / `superOnly` — legacy defence-in-depth
//     alongside the permission gate. Keeps super-sensitive tools like
//     `/super` out of a custom role that happens to be granted every
//     :view perm.
export const SIDEBAR_LINKS = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, hideForClient: true, guardOK: true, permission: "dashboard:view" },
  // iter305 — Ops-focused sibling of Overview. Same visual weight in the
  // sidebar so ops leads land on the metrics they read all day without
  // digging into Reports. Defaults surface every /metrics/operational tile;
  // admins can drag Overview tiles in via Customize.
  { to: "/ops-dashboard", label: "Ops Dashboard", icon: Gauge, end: true, hideForClient: true, permission: "ops_dashboard:view" },
  { to: "/portal", label: "Portal", icon: LayoutDashboard, end: true, clientOnly: true },
  // Officer's home — mirrors /guard-portal so a guard on any deep-linked
  // page (e.g. /dar) has a one-tap way back to their on-shift view.
  { to: "/guard-portal", label: "On Shift", icon: LayoutDashboard, end: true, guardOK: true, hideForClient: true },
  {
    label: "Clients",
    icon: Briefcase,
    hideForClient: true,
    group: "client-sites",
    children: [
      { to: "/clients", label: "Clients", icon: Briefcase, permission: "clients:view" },
      { to: "/sites", label: "Sites", icon: Building2, permission: "sites:view" },
      { to: "/settings/adhoc-requesters", label: "Temp WO Sites", icon: UserPlus, permission: "adhoc_requesters:view" },
      { to: "/sites/map", label: "Sites Map", icon: Map, permission: "sites:view" },
      { to: "/live-map", label: "Live Officers Map", icon: Radio, permission: "sites:view" },
    ],
  },
  {
    label: "Officers",
    icon: UserCog,
    hideForClient: true,
    group: "officers",
    guardOK: true,  // group visible so its guard-friendly children show up
    children: [
      { to: "/officers", label: "Officers", icon: Users, permission: "guards:view" },
      // Officers can only view + edit their own availability & vacation
      // requests — the pages already handle role-scoping internally.
      { to: "/availability", label: "Availability", icon: CalendarDays, guardOK: true, permission: "availability:view" },
      { to: "/vacation-requests", label: "Vacation Requests", icon: Palmtree, guardOK: true, permission: "vacation_requests:view" },
      // Moved from Operations — supervisor visits are about the supervisor,
      // not the shift roster.
      { to: "/supervisor-visits", label: "Supervisor Visits", icon: UserCheck, supAdminOnly: true, permission: "attendance:view" },
    ],
  },
  {
    label: "Operations",
    icon: Radio,
    hideForClient: true,
    group: "operations",
    guardOK: true,  // the group is visible; children opt in individually
    children: [
      { to: "/shifts", label: "Roster", icon: CalendarClock, permission: "shifts:view" },
      { to: "/attendance", label: "Attendance", icon: ClipboardCheck, permission: "attendance:view" },
      { to: "/open-shifts", label: "Open / Understaffed", icon: AlertTriangle, permission: "shifts:view" },
      { to: "/tardies", label: "Tardies", icon: Clock, permission: "tardies:view" },
      { to: "/adhoc-approvals", label: "Ad-hoc Approvals", icon: ClipboardList, permission: "adhoc_requesters:view" },
      { to: "/dispatch/log?new=1", label: "Dispatch", icon: Radio, hideForClient: true, permission: "shifts:edit" },
      { to: "/pending-approvals", label: "Pending Approvals", icon: ShieldCheck, hideForClient: true, badgeKey: "pending_approvals", permission: "shifts:edit" },
    ],
  },
  {
    label: "Reports & Payroll",
    icon: FileBarChart2,
    group: "reports-payroll",
    children: [
      // iter243 — Field reports moved out of Operations into Reports & Payroll
      // so operators land in one place when triaging or exporting client-facing
      // paperwork. Operations tab now stays focused on live shift signals.
      { to: "/incidents", label: "Incidents", icon: AlertOctagon, permission: "incidents:view" },
      { to: "/maintenance", label: "Maintenance", icon: Wrench, permission: "reports:view" },
      // The DAR page is the primary post-clock-out destination for officers.
      { to: "/dar", label: "Daily Activity", icon: FileSignature, guardOK: true, permission: "dar:view" },
      { to: "/tour-scans", label: "Tour Scans", icon: Route, permission: "tours:view" },
      { to: "/status-checks", label: "Status Checks", icon: ShieldCheck, permission: "attendance:view" },
      { to: "/breaks", label: "Breaks", icon: Coffee, permission: "attendance:view" },
      // iter244 — Live geofence breaches now surface as a tab inside Tardies;
      // this page becomes the historical record for compliance/exports.
      { to: "/geofence", label: "Geofence History", icon: MapPin, permission: "attendance:view" },
      // iter246 — Portal complaints belong with the rest of the field/report
      // triage so ops don't need to hop between top-level tabs.
      { to: "/portal-complaints", label: "Complaints", icon: MessageSquareWarning, adminScope: true, permission: "settings:view" },
      { to: "/reports", label: "Reports", icon: FileBarChart2, permission: "reports:view" },
      { to: "/payroll", label: "Payroll", icon: Wallet, hideForClient: true, permission: "payroll:view" },
    ],
  },
  {
    label: "Portal Config",
    icon: Inbox,
    hideForClient: true,
    adminScope: true,
    group: "client-portal",
    children: [
      { to: "/settings/client-portal", label: "Portal Config", icon: Sliders, adminScope: true, permission: "settings:view" },
      { to: "/settings/portal-templates", label: "Portal Templates", icon: Sparkles, adminScope: true, permission: "settings:view" },
      { to: "/settings/offices", label: "Agency Offices", icon: Building2, adminScope: true, permission: "offices:view" },
      { to: "/settings/sidebar-labels", label: "Sidebar Labels", icon: Tag, adminScope: true, permission: "settings:view" },
      { to: "/portal-activity", label: "Portal Activity", icon: Inbox, adminScope: true, permission: "settings:view" },
    ],
  },
  {
    label: "Admin & Logs",
    icon: ScrollText,
    hideForClient: true,
    adminScope: true,
    group: "admin-logs",
    children: [
      { to: "/users", label: "Users", icon: UserCog, adminScope: true, permission: "users:view" },
      { to: "/agency-settings", label: "Agency Settings", icon: Settings2, adminScope: true, permission: "settings:view" },
      { to: "/security-log", label: "Security Log", icon: ShieldAlert, adminOnly: true, permission: "security_log:view" },
      { to: "/settings/security", label: "Security Overview", icon: ShieldAlert, adminOnly: true, permission: "security_log:view" },
      { to: "/audit", label: "Audit Trail", icon: History, adminOnly: true, permission: "audit_log:view" },
      { to: "/notifications-log", label: "Notifications Log", icon: Mail, adminOnly: true, permission: "settings:view" },
      { to: "/settings/notifications", label: "Notification Console", icon: BellRing, adminScope: true, permission: "settings:view" },
      { to: "/data-retention", label: "Data Retention", icon: Database, adminScope: true, permission: "settings:view" },
      { to: "/dispatch/log", label: "Dispatch Log", icon: Send, hideForClient: true, permission: "shifts:view" },
      { to: "/super", label: "Super Console", icon: Globe, superOnly: true },
    ],
  },
  // iter323 — Help sits at the bottom of the sidebar. Available to
  // every internal role (guards included via `guardOK`). Client-tier
  // users live in `/portal` which has its own navigation; they reach
  // Help through the portal shell.
  { to: "/help", label: "Help / Training", icon: HelpCircle, guardOK: true },
];

export const SIDEBAR_STORAGE_KEYS = {
  COLLAPSED: "sentinel_sidebar_collapsed",
  OPS_GROUP: "sentinel_sidebar_ops_open",
  OPS_ORDER: "sentinel_sidebar_ops_order",
  LINKS_ORDER: "sentinel_sidebar_links_order",
  LAYOUT_MODE: "sentinel_sidebar_layout_mode",
};

export const LAYOUT_MODES = [
  { id: "default",   label: "Default",  hint: "Stock order — ignores your saved layout" },
  { id: "ops-first", label: "Ops First", hint: "Operations pinned to the top" },
  { id: "saved",     label: "Saved",     hint: "Your drag-and-drop layout" },
];

// Stable key per top-level link entry (NavLink or group)
export const topLinkKey = (l) => l.to || `group:${l.group || l.label}`;

export const OPS_PATHS = new Set([
  "/shifts", "/attendance", "/tardies", "/incidents", "/dar", "/supervisor-visits",
  "/adhoc-approvals",
]);

// NOC multi-screen preset — spawn Roster + Tardies + Incidents + DAR into 4
// separate browser windows (matching the per-page /fullscreen routes). Each
// window is sized to roughly a quarter of the current screen and positioned in
// a 2×2 grid so a dispatcher can drag them onto multiple monitors.
export const OPS_POPOUT_TARGETS = [
  { url: "/shifts/fullscreen", label: "Roster" },
  { url: "/tardies/fullscreen", label: "Tardies" },
  { url: "/incidents/fullscreen", label: "Incidents" },
  { url: "/dar/fullscreen", label: "Daily Activity" },
];

export function popOutOpsWindows() {
  const sw = window.screen?.availWidth || window.innerWidth || 1280;
  const sh = window.screen?.availHeight || window.innerHeight || 800;
  const dw = Math.floor(sw / 2);
  const dh = Math.floor(sh / 2);
  const blocked = [];
  OPS_POPOUT_TARGETS.forEach((t, i) => {
    const saved = readGeometry(t.url);
    const w = saved?.w ?? dw;
    const h = saved?.h ?? dh;
    const left = saved?.x ?? (i % 2) * dw;
    const top = saved?.y ?? Math.floor(i / 2) * dh;
    const win = window.open(
      t.url,
      `irisguard-${t.url}`,
      `noopener,width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!win) blocked.push(t.label);
  });
  return blocked;
}
